import * as React from "react";
import { useState, useRef, useEffect } from "react";
import isEqual from "lodash/isEqual";
import {
  mediaQueryColorScheme,
  resolveThemeMode,
  getAccent,
  getAccentText,
} from "../../helpers/style";
import { lightThemeClass, darkThemeClass } from "../../styles/themes.css";
import { isSSR } from "../../helpers/platform";
import { store } from "../../models/store";
import { ThemeVariant } from "../../models/system.model";
import { assignThemeVars } from "../../styles/override/override";
import type { ThemeDef } from "../../styles/override/override.types";
import type { ThemeProviderProps } from "./theme-provider.types";

function ThemeProvider(props: ThemeProviderProps) {
  const cleanupRef = useRef<() => void>(null);
  const [preferredMode, setPreferredMode] = useState(() => null);

  const [isMounted, setIsMounted] = useState(() => false);

  const [localCustomTheme, setLocalCustomTheme] = useState(() => null);

  const [localThemeDefs, setLocalThemeDefs] = useState(() => []);

  function isControlled() {
    return props.themeMode != null;
  }

  function isReady() {
    return preferredMode && isMounted;
  }

  function themeClass() {
    if (isControlled()) {
      if (props.themeMode === "system") {
        const finalThemeMode = isReady() ? preferredMode : props.themeMode;
        return finalThemeMode === "dark" ? darkThemeClass : lightThemeClass;
      }
      return props.themeMode === "dark" ? darkThemeClass : lightThemeClass;
    }
    return store.getState().themeClass;
  }

  function storeState() {
    return store.getState();
  }

  function theme() {
    return storeState().theme;
  }

  function lightQuery() {
    if (isSSR()) return null;
    return window?.matchMedia?.(mediaQueryColorScheme(`light`));
  }

  function darkQuery() {
    if (isSSR()) return null;
    return window?.matchMedia?.(mediaQueryColorScheme(`dark`));
  }

  function isDark() {
    return !!darkQuery?.()?.matches;
  }

  function isLight() {
    return !!lightQuery?.()?.matches;
  }

  useEffect(() => {
    setIsMounted(true);

    // Dont set global theme mode if in controlled mode
    if (!isControlled()) {
      resolveThemeMode(props.defaultTheme);
    }
    const darkListener = ({ matches }: MediaQueryListEvent) => {
      if (matches) {
        setPreferredMode("dark");
      }
    };
    const lightListener = ({ matches }: MediaQueryListEvent) => {
      if (matches) {
        setPreferredMode("light");
      }
    };
    if (darkQuery() && lightQuery()) {
      if (
        typeof darkQuery().addEventListener === "function" &&
        typeof lightQuery().addEventListener === "function"
      ) {
        darkQuery?.()?.addEventListener("change", darkListener);
        lightQuery?.()?.addEventListener("change", lightListener);
      }
    }
    cleanupRef.current = () => {
      darkQuery?.()?.removeEventListener("change", darkListener);
      lightQuery?.()?.removeEventListener("change", lightListener);
    };
  }, []);

  useEffect(() => {
    if (!isReady()) return;
    const themeMode = storeState().themeMode;
    if (themeMode === "system") {
      return storeState().setThemeMode(themeMode);
    }
  }, [preferredMode, theme(), isMounted]);
  useEffect(() => {
    const finalThemeDefs = props.themeDefs ?? [];
    const isValidThemeDefs =
      Array.isArray(props.themeDefs) && finalThemeDefs.length > 0;
    if (!isValidThemeDefs) {
      return;
    }

    // Only set global custom themes if props.themeMode is not controlled
    // controlled props.themeMode usage means that the user is managing nested theme
    if (isControlled()) {
      return;
    } else {
      if (!isEqual(storeState().themeDefs, finalThemeDefs)) {
        storeState().setThemeDefs(finalThemeDefs);
      }
    }
  }, [props.themeDefs]);
  useEffect(() => {
    if (!props.customTheme) return;

    // TODO: handle custom theme for controlled mode
    storeState().setCustomTheme(props.customTheme);
  }, [props.customTheme]);
  useEffect(() => {
    const overrideStyleManager = storeState().overrideStyleManager;
    if (!overrideStyleManager) return;
    overrideStyleManager.update(props.overrides, null);
  }, [props.overrides]);
  useEffect(() => {
    // Skip if accent is not provided
    if (!props.accent) return;
    const prevAccent = storeState().themeAccent;
    const currentColorMode = storeState().theme;
    if (prevAccent !== props.accent) {
      storeState().setThemeAccent(props.accent ?? "blue");
      assignThemeVars(
        {
          colors: {
            // @ts-ignore
            accent: getAccent(props.accent, currentColorMode ?? "light"),
            // @ts-ignore
            accentText: getAccentText(currentColorMode ?? "light"),
          },
        },
        currentColorMode
      );
    }
  }, [props.accent]);

  useEffect(() => {
    return () => {
      if (typeof cleanupRef.current === "function") {
        cleanupRef.current();
      }
    };
  }, []);

  return (
    <div
      data-is-controlled={isControlled()}
      data-interchain-theme-mode={
        props.themeMode == null ? undefined : props.themeMode
      }
      style={{
        visibility: isMounted ? "visible" : "hidden",
      }}
      className={themeClass()}
    >
      {props.children}
    </div>
  );
}

export default ThemeProvider;
